'use client';

import type React from 'react';
import { type ChangeEvent, useActionState, useState } from 'react';
import ButtonPrimary from '@/app/_lib/ui/ButtonPrimary';
import { submitContactForm } from '../_actions/contactActions';
import MarkdownWrapper from './MarkdownWrapper';

// Define the state structure for useActionState locally
interface IFormState {
  message: string;
  error: boolean;
  submitted: boolean;
  fieldErrors?: {
    fullName?: string;
    email?: string;
    phone?: string;
    message?: string;
  };
}

// Define props for the component, accepting RAW MARKDOWN content
interface ContactProps {
  contactMdContent: string;
  jobsMdContent: string;
}

// Define initial state locally for the form action
const initialState: IFormState = {
  message: '',
  error: false,
  submitted: false,
  fieldErrors: {},
};

// Phone formatting function remains the same
const phoneFormat = (str: string): string => {
  const num = str ? str.replace(/[^0-9]+/gm, '') : '';
  const areaCode = num.substring(0, 3);
  const middle = num.substring(3, 6);
  const last = num.substring(6, 10);
  const ext = num.length > 10 ? num.substring(10) : '';

  let formatted = '';
  if (num.length > 6) {
    formatted = `(${areaCode}) ${middle}-${last}`;
  } else if (num.length > 3) {
    formatted = `(${areaCode}) ${middle}`;
  } else if (num.length > 0) {
    formatted = `(${areaCode}`;
  }

  return formatted + (ext ? ` Ext. ${ext}` : '');
};

// This remains a Client Component
const Contact = ({ contactMdContent, jobsMdContent }: ContactProps) => {
  const [state, formAction, isPending] = useActionState(submitContactForm, initialState);
  const [phoneInputValue, setPhoneInputValue] = useState<string>('');

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPhoneInputValue(phoneFormat(e.target.value));
  };

  if (state.submitted && phoneInputValue !== '') {
    setPhoneInputValue('');
  }

  return (
    <section
      className='bg-gradient-to-b from-white py-16 md:py-24 dark:from-gray-900 dark:to-gray-950'
      id='contact-us'
      aria-labelledby='contact-heading'
    >
      <div className='container mx-auto px-4 sm:px-6'>
        <div
          className='animate-when-visible mx-auto max-w-4xl text-center'
          style={{ '--animation-duration': '0.6s' } as React.CSSProperties}
          ref={(el) => {
            if (el) {
              const observer = new IntersectionObserver(
                (entries) => {
                  entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                      entry.target.classList.add('is-visible');
                      observer.unobserve(entry.target);
                    }
                  });
                },
                { threshold: 0.1 },
              );
              observer.observe(el);
            }
          }}
        >
          <div className='mb-12'>
            <MarkdownWrapper content={contactMdContent} className='mx-auto' />
          </div>

          {/* Form Status Message */}
          {(state.submitted || state.error) && (
            <div
              className={`animate-fade-in mb-8 rounded-xl p-6 shadow-lg ${
                state.submitted && !state.error
                  ? 'border border-green-100 bg-green-50 dark:border-green-800 dark:bg-green-900/30'
                  : 'border border-red-100 bg-red-50 dark:border-red-800 dark:bg-red-900/30'
              }`}
              role='alert'
              aria-live='assertive'
            >
              {/* ... status message content ... */}
              {state.submitted && !state.error ? (
                <div className='flex flex-col items-center'>
                  <div className='mb-4 rounded-full bg-green-100 p-3 dark:bg-green-800/50'>
                    <svg
                      className='h-6 w-6 text-green-600 dark:text-green-400'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      aria-hidden={true}
                    >
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                  </div>
                  <h3 className='mb-2 text-xl font-semibold text-green-700 dark:text-green-400'>{state.message}</h3>
                </div>
              ) : (
                <div className='flex flex-col items-center'>
                  <div className='mb-4 rounded-full bg-red-100 p-3 dark:bg-red-800/50'>
                    <svg
                      className='h-6 w-6 text-red-600 dark:text-red-400'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      aria-hidden={true}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                      />
                    </svg>
                  </div>
                  <h3 className='mb-2 text-xl font-semibold text-red-700 dark:text-red-400'>
                    Oops! There was a problem.
                  </h3>
                  <p className='text-red-600 dark:text-red-400'>{state.message}</p>
                </div>
              )}
            </div>
          )}

          {/* Contact Form */}
          {!state.submitted && (
            <div className='animate-fade-in-delay rounded-xl bg-white p-8 shadow-lg dark:bg-gray-800'>
              <form name='Contact' aria-label='Contact form' className='space-y-6' action={formAction} noValidate>
                {/* Contact Information Fieldset */}
                <fieldset>
                  <legend className='mb-4 border-b border-gray-200 px-2 pb-2 text-lg font-medium text-gray-800 dark:border-gray-700 dark:text-gray-200'>
                    Your Contact Information
                  </legend>

                  {/* Full Name Input */}
                  <div>
                    <label
                      htmlFor='fullName'
                      className='block text-left text-base font-medium text-gray-700 dark:text-gray-300'
                    >
                      Name
                    </label>
                    <div className='mt-1'>
                      <input
                        id='fullName'
                        name='fullName'
                        type='text'
                        className={`block w-full border px-4 py-3 ${state.fieldErrors?.fullName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-primary dark:focus:ring-primary-light focus:border-primary dark:focus:border-primary-light rounded-lg bg-white text-base text-gray-900 placeholder-gray-400 shadow-sm transition-colors duration-200 focus:ring-2 focus:outline-none dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500`}
                        spellCheck={true}
                        autoCorrect='on'
                        autoComplete='name'
                        placeholder='Your full name'
                        aria-invalid={!!state.fieldErrors?.fullName}
                        aria-describedby={state.fieldErrors?.fullName ? 'fullName-error' : undefined}
                        required
                      />
                    </div>
                    {state.fieldErrors?.fullName && (
                      <p id='fullName-error' className='mt-2 text-left text-sm text-red-600'>
                        {state.fieldErrors.fullName}
                      </p>
                    )}
                  </div>

                  {/* Email and Phone Row */}
                  <div className='mt-4 grid grid-cols-1 gap-6 md:grid-cols-2'>
                    {/* Email Input */}
                    <div>
                      <label
                        htmlFor='email'
                        className='block text-left text-base font-medium text-gray-700 dark:text-gray-300'
                      >
                        Email
                      </label>
                      <div className='mt-1'>
                        <input
                          id='email'
                          name='email'
                          type='email'
                          className={`block w-full border px-4 py-3 ${state.fieldErrors?.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-primary dark:focus:ring-primary-light focus:border-primary dark:focus:border-primary-light rounded-lg bg-white text-base text-gray-900 placeholder-gray-400 shadow-sm transition-colors duration-200 focus:ring-2 focus:outline-none dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500`}
                          spellCheck='false'
                          autoCorrect='off'
                          autoComplete='email'
                          placeholder='your.email@example.com'
                          aria-invalid={!!state.fieldErrors?.email}
                          aria-describedby={state.fieldErrors?.email ? 'email-error' : undefined}
                          required
                        />
                      </div>
                      {state.fieldErrors?.email && (
                        <p id='email-error' className='mt-2 text-left text-sm text-red-600'>
                          {state.fieldErrors.email}
                        </p>
                      )}
                    </div>

                    {/* Phone Input */}
                    <div>
                      <label
                        htmlFor='phone'
                        className='block text-left text-base font-medium text-gray-700 dark:text-gray-300'
                      >
                        Phone <span className='text-gray-500 dark:text-gray-400'>(Optional)</span>
                      </label>
                      <div className='mt-1'>
                        <input
                          id='phone'
                          name='phone'
                          type='tel'
                          className={`block w-full border px-4 py-3 ${state.fieldErrors?.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-primary dark:focus:ring-primary-light focus:border-primary dark:focus:border-primary-light rounded-lg bg-white text-base text-gray-900 placeholder-gray-400 shadow-sm transition-colors duration-200 focus:ring-2 focus:outline-none dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500`}
                          spellCheck='false'
                          autoCorrect='off'
                          autoComplete='tel'
                          placeholder='(123) 456-7890'
                          value={phoneInputValue}
                          onChange={handlePhoneChange}
                          aria-invalid={!!state.fieldErrors?.phone}
                          aria-describedby={state.fieldErrors?.phone ? 'phone-error' : undefined}
                        />
                      </div>
                      {state.fieldErrors?.phone && (
                        <p id='phone-error' className='mt-2 text-left text-sm text-red-600'>
                          {state.fieldErrors.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </fieldset>

                {/* Message Fieldset */}
                <fieldset>
                  <legend className='mb-4 border-b border-gray-200 px-2 pb-2 text-lg font-medium text-gray-800 dark:border-gray-700 dark:text-gray-200'>
                    Your Message
                  </legend>

                  {/* Message Textarea */}
                  <div>
                    <label
                      htmlFor='message'
                      className='block text-left text-base font-medium text-gray-700 dark:text-gray-300'
                    >
                      Message/Question
                    </label>
                    <div className='mt-1'>
                      <textarea
                        id='message'
                        name='message'
                        rows={6}
                        className={`block w-full border px-4 py-3 ${state.fieldErrors?.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-primary dark:focus:ring-primary-light focus:border-primary dark:focus:border-primary-light rounded-lg bg-white text-base text-gray-900 placeholder-gray-400 shadow-sm transition-colors duration-200 focus:ring-2 focus:outline-none dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500`}
                        spellCheck={true}
                        autoCorrect='on'
                        placeholder="Please provide your question or message. We'll get back to you as soon as possible."
                        aria-invalid={!!state.fieldErrors?.message}
                        aria-describedby={state.fieldErrors?.message ? 'message-error' : undefined}
                        required
                      />
                    </div>
                    {state.fieldErrors?.message && (
                      <p id='message-error' className='mt-2 text-left text-sm text-red-600'>
                        {state.fieldErrors.message}
                      </p>
                    )}
                  </div>
                </fieldset>

                <div className='pt-4 text-center'>
                  <ButtonPrimary type='submit' disabled={isPending} aria-disabled={isPending} className='group'>
                    {/* ... button content ... */}
                    {isPending ? (
                      <>
                        <svg
                          className='mr-3 -ml-1 h-5 w-5 animate-spin'
                          fill='none'
                          viewBox='0 0 24 24'
                          aria-hidden={true}
                        >
                          <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Message
                        <svg
                          className='-mr-1 ml-2 h-5 w-5 transition-transform group-hover:translate-x-1'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                          aria-hidden={true}
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M14 5l7 7m0 0l-7 7m7-7H3'
                          />
                        </svg>
                      </>
                    )}
                  </ButtonPrimary>
                </div>
              </form>

              <div className='mt-8 text-center text-gray-600 dark:text-gray-400'>
                {}
                <p>Your privacy is important to us. We'll never share your information with third parties.</p>
              </div>
            </div>
          )}

          {/* Job Opportunities Section */}
          <div
            id='jobs'
            className='animate-when-visible mt-16'
            style={{ '--animation-duration': '0.6s' } as React.CSSProperties}
            ref={(el) => {
              if (el) {
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                      }
                    });
                  },
                  { threshold: 0.1 },
                );
                observer.observe(el);
              }
            }}
          >
            <MarkdownWrapper content={jobsMdContent} className='mx-auto' />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
